'use client';

import { cn } from '@morpho-org/ui-v6/utils';
import { SegmentedControlItem, SegmentedControlList, Tabs } from '@repo/ui';
import { useCallback } from 'react';
export interface WrapSegmentProps {
  /** Id / `data-testid` stem, e.g. `"market-supply"` → `market-supply-wrap` + `market-supply-wrap-segment`. */
  id: string;
  /** Flow flag — the selector is hidden when the operation's flow is off. */
  enabled: boolean;
  /** Whether the funded token is the chain's wNative (only then can native funding wrap into it). */
  isWNative: boolean;
  /** Native balance left after the gas reserve; hidden unless there is something to wrap. */
  wrappableNativeBalance: bigint;
  /** Reserves the selector's space (without showing it) while the balance or gas reserve resolves. */
  isNativeBalancePending?: boolean;
  /** The wNative ERC20 symbol (the funded token). */
  wrappedSymbol: string | undefined;
  /** The chain's native-currency symbol. */
  nativeSymbol: string;
  /** `true` ⇒ pay with native currency (wrap it); `false` ⇒ pay with the wNative ERC20. */
  isWrapping: boolean;
  onWrappingChange: (isWrapping: boolean) => void;
  /** Disables the control while balances / gas reserve resolve. */
  disabled?: boolean;
}
type WrapSegmentValue = 'wrapped' | 'native';

/**
 * "Pay with" funding-token selector for the market native-wrap flows. v6 native funding is
 * exclusive — the user pays with the native currency OR the wNative ERC20, never a blend — so the
 * choice is a two-option segmented control rather than a boolean "allow wrapping" toggle. Shared by
 * the market supply/borrow, lend and repay panels, the position-manager dialogs and the vault
 * deposit flows; renders nothing unless the funded token is wNative and the wallet holds native
 * currency to wrap after the gas reserve, except while native funding is already selected. With
 * `isNativeBalancePending` it keeps an invisible placeholder of the same size while the balance or
 * gas reserve is not known yet, so the selector doesn't push the content below it down.
 */
export function WrapSegment({
  id,
  enabled,
  isWNative,
  wrappableNativeBalance,
  isNativeBalancePending = false,
  wrappedSymbol,
  nativeSymbol,
  isWrapping,
  onWrappingChange,
  disabled
}: WrapSegmentProps) {
  const handleValueChange = useCallback((value: string) => {
    if (value === 'wrapped' || value === 'native') onWrappingChange(value === 'native');
  }, [onWrappingChange]);
  if (!enabled || !isWNative || !wrappedSymbol) return null;
  const hasWrappableNative = wrappableNativeBalance > 0n;
  const isShown = hasWrappableNative || isWrapping;
  if (!isShown && !isNativeBalancePending) return null;
  const segmentId = `${id}-wrap`;
  const value_0: WrapSegmentValue = isWrapping ? 'native' : 'wrapped';
  return <div className={cn('mt-xs gap-2xs flex flex-row items-center justify-between', !isShown && 'invisible')} aria-hidden={!isShown || undefined} inert={!isShown || undefined}>
      <span id={`${segmentId}-label`} className="text-2xs text-foreground">
        Pay with
      </span>
      <Tabs value={value_0} onValueChange={handleValueChange} className="gap-0">
        <SegmentedControlList id={segmentId} data-testid={`${segmentId}-segment`} aria-labelledby={`${segmentId}-label`}>
          <SegmentedControlItem value="wrapped" data-testid={`${segmentId}-option-wrapped`} disabled={disabled}>
            {wrappedSymbol}
          </SegmentedControlItem>
          <SegmentedControlItem value="native" data-testid={`${segmentId}-option-native`} disabled={disabled}>
            {nativeSymbol}
          </SegmentedControlItem>
        </SegmentedControlList>
      </Tabs>
    </div>;
}